import React, { useState, useEffect, useRef } from "react";
import {
  ArrowUpDown,
  Book,
  MoreVertical,
  UserCheck,
  UserX,
  Edit,
  PhoneCall,
  MessageCircle,
  HelpCircle,
  X,
  Tag,
  MessageSquare,
  Video,
} from "lucide-react";
import Modal from "react-modal";
import InterviewActionButtons from "./components/InterviewActionButtons";
import NoteAddForm from "./NoteForm";
import { api, API_BASE_URL, instantVideoCallApi } from "./utils/api";
import {
  hireJobseeker,
  rejectJobseeker,
  chaseJobseekerForQuestionnaire,
  capitalizeWords,
  sortedJobseekers,
} from "./utils";
import { useNotification } from "./CustomNotificationSystem";
import MessageInterface from "./MessageInterface";
import { getNameInitials } from "./utils";
import { Tooltip as ToolTipHtml } from "react-tooltip";
import JobseekerStatusMultiSelect from "./components/JobseekerStatusMultiSelect";
import TagInterface from "./components/TagInterface";
import TagComponent from "./components/Tags";
import { useTag } from "./services/Tags";
import Select from "react-select";
import ExpandedJobseekerView from "./components/ExpandJobseeker";
import InstantVideoCallModal from "./components/InstantVideoCallModal";

// Placeholder function for API call
/*const fetchJobseekers = async (jobId) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const filteredJobseekers = dummyJobseekers.filter(js => js.jobId === jobId);
      resolve(filteredJobseekers);
    }, 500);
  });
};*/

const ActionMenu = ({ jobseekerId, jobseekerName, jobseeker, onAction }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropupMode, setDropupMode] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const updateDropupMode = () => {
      if (buttonRef.current) {
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const spaceBelow = windowHeight - buttonRect.bottom;
        // If space below is less than 320px (approximate menu height), switch to dropup
        setDropupMode(spaceBelow < 320);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", updateDropupMode);
    window.addEventListener("resize", updateDropupMode);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", updateDropupMode);
      window.removeEventListener("resize", updateDropupMode);
    };
  }, []);

  const handleAction = (action) => {
    onAction(action, jobseekerId, jobseekerName, jobseeker);
    setIsOpen(false);
  };

  const actions = [
    { name: "Add Notes", icon: <Edit size={16} className="mr-2" /> },
    {
      name: "Ask Further Questions",
      icon: <HelpCircle size={16} className="mr-2" />,
    },
    { name: "Call", icon: <PhoneCall size={16} className="mr-2" /> },
    {
      name: "Chase up Questionnaire",
      icon: <MessageCircle size={16} className="mr-2" />,
    },
    {
      name: "Generate Instant Video Call Link",
      icon: <Video size={24} className="mr-2" />,
    },
    {
      name: "Tag",
      icon: <Tag size={16} className="mr-2" />,
    },
    {
      name: "Invite to Interview",
      icon: <UserCheck size={16} className="mr-2" />,
    },
    { name: "Mark as Hired", icon: <UserCheck size={16} className="mr-2" /> },
    { name: "Reject", icon: <UserX size={16} className="mr-2" /> },
    {
      name: "Message",
      icon: <MessageSquare size={16} className="mr-2" />,
    },
  ].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="relative" ref={menuRef}>
      <button
        ref={buttonRef}
        onClick={() => {
          const buttonRect = buttonRef.current.getBoundingClientRect();
          const windowHeight = window.innerHeight;
          const spaceBelow = windowHeight - buttonRect.bottom;
          setDropupMode(spaceBelow < 320);
          setIsOpen(!isOpen);
        }}
        className="p-2 md:p-3 rounded-full hover:bg-gray-200 focus:outline-none min-w-[44px] min-h-[44px] flex items-center justify-center"
      >
        <MoreVertical size={20} className="md:w-6 md:h-6" strokeWidth={2.5} />
      </button>
      {isOpen && (
        <div
          className={`absolute ${
            dropupMode ? "bottom-full mb-2" : "top-full mt-2"
          } right-0 w-56 md:w-48 bg-white rounded-md shadow-lg z-50`}
        >
          <div className="py-1">
            {actions.map(({ name, icon }) => (
              <button
                key={name}
                className="block w-full text-left px-4 py-3 md:py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center min-h-[44px] md:min-h-0"
                onClick={() => handleAction(name)}
              >
                {icon} {name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

{
  /* EXPAND JOBSEEKER */
}

const JobseekerList = ({
  selectedJob,
  expandedJobseekerId,
  initialJobseekers,
  jobs,
  currentUser,
  currentCompany,
}) => {
  const [jobseekers, setJobseekers] = useState(initialJobseekers || []);
  const [expandedJobseeker, setExpandedJobseeker] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedJobseekers, setSelectedJobseekers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [filterStatus, setFilterStatus] = useState(["all"]);
  const [filterQuestionnaireStatus, setFilterQuestionnaireStatus] = useState([
    "all",
  ]);
  const handleCloseModal = () => {
    setModalState({ isOpen: false, type: null, jobSeekerId: null });
  };

  const [modalState, setModalState] = useState({
    isOpen: false,
    type: null,
    //jobSeekerId: null,
  });
  const [interviewData, setInterviewData] = useState({});
  const [instantCallData, setInstantCallData] = useState(null);
  const [jobseekerAverageScore, setJobseekerAverageScore] = useState([]);
  const notify = useNotification();
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [sortName, setSortName] = useState(false);
  const [sortApplied, setSortApplied] = useState(false);
  const [sortQStatus, setSortQStatus] = useState(false);
  const [sortScore, setSortScore] = useState(false);
  const [sortOrder, setSortOrder] = useState("asc");
  const [tags, setTags] = useState([]);
  const [filterTags, setFilterTags] = useState(["all"]);
  const { deleteJobseekerTag, getTagsByCompany } = useTag();
  const [bulkSelect] = useState("");

  useEffect(() => {
    const jobseekers = sortedJobseekers(initialJobseekers);
    setJobseekers(jobseekers || []);
    setIsLoading(false);

    const getJobseekerAverageScore = () => {
      let average_score = [];
      let total_count = 0;
      for (let jobseeker of initialJobseekers) {
        if (jobseeker.detailedInfo?.summary_scoring) {
          const summary_scoring = jobseeker?.detailedInfo?.summary_scoring;
          total_count++;
          for (let data_point in summary_scoring) {
            if (average_score[data_point]) {
              average_score[data_point] += parseInt(
                summary_scoring[data_point],
              );
            } else {
              average_score[data_point] = parseInt(summary_scoring[data_point]);
            }
          }
        }
      }

      let average = {};
      for (let data_point in average_score) {
        average[data_point] = (
          parseInt(average_score[data_point]) / total_count
        ).toFixed(1);
      }
      setJobseekerAverageScore(average);
    };

    const getCompanyTags = async () => {
      const companyTags = await getTagsByCompany(currentCompany._id);
      setTags(companyTags.map((tag) => ({ label: tag.name, value: tag.name })));
    };

    getCompanyTags();
    getJobseekerAverageScore();
  }, [selectedJob, initialJobseekers, currentCompany]);

  useEffect(() => {
    if (expandedJobseekerId) {
      setExpandedJobseeker(expandedJobseekerId);
      const jobseekerElement = document.getElementById(
        `jobseeker-${expandedJobseekerId}`,
      );
      if (jobseekerElement) {
        jobseekerElement.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, [expandedJobseekerId]);

  useEffect(() => {
    if (selectAll) {
      setSelectedJobseekers(
        jobseekers.map((js) => {
          return { id: js._id, name: js.name };
        }),
      );
    } else {
      setSelectedJobseekers([]);
    }
  }, [selectAll, jobseekers]);

  useEffect(() => {
    const name = jobs.filter((job) => {
      if (job._id === selectedJob) {
        return true;
      }
      return false;
    });
    setInterviewData({
      id: selectedJob,
      name: name[0]?.jobReference,
      candidates: selectedJobseekers,
    });

    
  }, [selectedJobseekers, selectedJob, jobs]);

  const getStatusColor = (status) => {
    if (!status) return "";
    switch (status.toLowerCase()) {
      case "not_started":
        return "bg-gray-200 text-gray-800";
      case "in_progress":
        return "bg-yellow-200 text-yellow-800";
      case "completed":
        return "bg-green-200 text-green-800";
      default:
        return "bg-gray-200 text-gray-800";
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "border-green-500 text-green-700";
    if (score >= 60) return "border-yellow-500 text-yellow-700";
    return "border-red-500 text-red-700";
  };

  const getApplicationStatusColor = (status) => {
    if (!status) return "";
    switch (status.toLowerCase()) {
      case "applied":
        return "bg-blue-200 text-blue-800";
      case "interview":
        return "bg-purple-200 text-purple-800";
      case "hired":
        return "bg-green-200 text-green-800";
      case "rejected":
        return "bg-red-200 text-red-800";
      case "pending":
        return "bg-yellow-200 text-gray-800";
      default:
        return "bg-gray-200 text-gray-800";
    }
  };

  const handleCheckboxChange = (jobseekerId, jobseekerName, jobseeker) => {
    setSelectedJobseekers((prev) => {
      // Check if the jobseeker is already in the array
      const existingIndex = prev.findIndex((js) => js.id === jobseekerId);

      if (existingIndex !== -1) {
        // If found, remove the jobseeker
        return prev.filter((js) => js.id !== jobseekerId);
      } else {
        // If not found, add the jobseeker
        return [
          ...prev,
          { id: jobseekerId, name: jobseekerName, jobseeker: jobseeker },
        ];
      }
    });
  };

  const handleSelectAllChange = () => {
    setSelectAll(!selectAll);
  };

  const handleGenerateInstantVideoCall = async (selectedJobseekers) => {
    try {
      if (selectedJobseekers.length === 0) {
        notify("Please select at least one jobseeker", "error");
        return;
      }

      // For now, handle only the first selected jobseeker
      const jobseeker = selectedJobseekers[0];
      const jobseekerData = jobseeker.jobseeker || jobseeker;

      // Prepare company data
      const companyData = {
        companyId: currentCompany._id,
        jobId: selectedJob,
        jobTitle: jobs.find(job => job._id === selectedJob)?.jobReference || 'Instant Interview',
        interviewType: 'technical'
      };

      console.log('Company data prepared:', companyData);

      // Create instant video call (owner data will be taken from authenticated user)
      const result = await instantVideoCallApi.createInstantVideoCall(
        jobseekerData,
        companyData
      );

      setInstantCallData(result);
      setModalState({ isOpen: true, type: "instantCall" });
      notify("Instant video call link generated successfully!", "success");

    } catch (error) {
      console.error("Failed to generate instant video call:", error);
      notify("Failed to generate instant video call link", "error");
    }
  };

  const handleSendInstantCallEmail = async (roomId, candidateId, email) => {
    try {
      const result = await instantVideoCallApi.sendInstantVideoCallEmail(roomId, candidateId, email);
      notify("Email sent successfully!", "success");
      return result;
    } catch (error) {
      console.error("Failed to send email:", error);
      notify("Failed to send email", "error");
      throw error;
    }
  };

  const handleAction = async (
    action,
    jobseekerId,
    jobseekerName,
    jobseeker,
  ) => {
    // First ensure we have the correct selection
    let newSelection;

    if (selectedJobseekers.length > 0) {
      newSelection = selectedJobseekers;
    }

    if (jobseeker) {
      // if the menu is called from the single jobseeker menu
      newSelection = [
        { id: jobseekerId, name: jobseekerName, jobseeker: jobseeker },
      ];
    }
    setSelectedJobseekers(newSelection);

    switch (action) {
      case "Invite to Interview":
        const interviewData = {
          id: selectedJob,
          name: jobs.find((job) => job._id === selectedJob)?.jobReference,
          candidates: newSelection,
        };
        setInterviewData(interviewData);
        setModalState({ isOpen: true, type: "interview" });
        break;

      case "Add Notes":
        setModalState({ isOpen: true, type: "note" });
        break;

      case "Call":
        window.location = `tel:${jobseeker.phone}`;
        break;

      case "Mark as Hired":
        if (window.confirm("Are you sure?")) {
          await hireJobseeker(jobseekerId);
          notify("Jobseeker is marked as Hired", "success");
        }
        break;

      case "Reject":
        if (window.confirm("Are you sure?")) {
          await rejectJobseeker(jobseekerId);
          notify("Jobseeker is marked as Rejected", "success");
        }
        break;

      case "Message":
        setModalState({ isOpen: true, type: "message" });
        break;

      case "Chase up Questionnaire":
        if (window.confirm("Are you sure?")) {
          try {
            const response = await chaseJobseekerForQuestionnaire(
              newSelection,
              selectedJob,
            );
            notify(
              response.status
                ? "Questionnaire invite is sent"
                : response.message || "Questionnaire not found for the job",
              response.status ? "success" : "error",
            );
          } catch (error) {
            notify("Failed to send questionnaire", "error");
            console.error("Questionnaire error:", error);
          }
        }
        break;
      case "Tag":
        setModalState({ isOpen: true, type: "tag" });
        break;

      case "Generate Instant Video Call Link":
        await handleGenerateInstantVideoCall(newSelection);
        break;

      default:
        console.log("Unhandled action:", action);
    }
    /*
  } else {
    // Handle actions with existing selection
    switch (action) {
      case "Invite to Interview":
        const interviewData = {
          id: selectedJob,
          name: jobs.find((job) => job._id === selectedJob)?.jobReference,
          candidates: selectedJobseekers,
        };
        setInterviewData(interviewData);
        setModalState({ isOpen: true, type: "interview" });
        break;

      case "Add Notes":
        setModalState({ isOpen: true, type: "note" });
        break;

      case "Call":
        window.location = `tel:${jobseeker.phone}`;
        break;

      case "Mark as Hired":
        if (window.confirm("Are you sure?")) {
          await hireJobseeker(jobseekerId);
          notify("Jobseeker is marked as Hired", "success");
        }
        break;

      case "Reject":
        if (window.confirm("Are you sure?")) {
          await rejectJobseeker(jobseekerId);
          notify("Jobseeker is marked as Rejected", "success");
        }
        break;

      case "Message":
        setModalState({ isOpen: true, type: "message" });
        break;

      case "Chase up Questionnaire":
        if (window.confirm("Are you sure?")) {
          try {
            const status = await chaseJobseekerForQuestionnaire(
              selectedJobseekers,
              selectedJob,
            );
            notify(
              status
                ? "Questionnaire invite is sent"
                : "Questionnaire not found for the job",
              status ? "success" : "error",
            );
          } catch (error) {
            notify("Failed to send questionnaire", "error");
            console.error("Questionnaire error:", error);
          }
        }
        break;
      case "Tag":
        setModalState({ isOpen: true, type: "tag" });
        break;

      default:
        console.log("Unhandled action:", action);
    }
  }
  */
  };

  const filteredJobseekers = jobseekers
    .filter(
      (js) =>
        filterStatus.includes("all") ||
        filterStatus.includes(js.applicationStatus),
    )
    .filter(
      (js) =>
        filterQuestionnaireStatus.includes("all") ||
        filterQuestionnaireStatus.includes(js.questionnaireStatus),
    )
    .filter(
      (js) =>
        filterTags.includes("all") ||
        js.tags.some((tag) => filterTags.includes(tag.tag.name)), // Use `.some()` instead of `.filter()`
    );
  if (isLoading) {
    return <div className="text-center py-4">Loading jobseekers...</div>;
  }

  if (error) {
    return <div className="text-center py-4 text-red-500">{error}</div>;
  }

  const handleNoteSubmit = async (newNote) => {
    try {
      // Send the newNote to your API endpoint
      const token = localStorage.getItem("token");
      await api.post(`/api/notes`, newNote, {
        headers: { Authorization: `Bearer ${token}` },
      });
      notify("Note is added to Selected Jobseeker", "success");
    } catch (error) {
      console.error("Error adding note:", error);
    }
  };

  const handleAddTags = (tagList) => {
    const jobseekers = filteredJobseekers.map((jobseeker) => {
      const newTags = tagList.filter((tag) => {
        return tag.jobseeker === jobseeker._id ? true : false;
      });

      if (newTags.length > 0) {
        jobseeker.tags.push(...newTags);
      }
      return jobseeker;
    });
    setJobseekers(jobseekers);
  };
  const renderModalContent = () => {
    switch (modalState.type) {
      case "interview":
        return (
          <InterviewActionButtons
            interviewData={interviewData}
            handleCloseModal={handleCloseModal}
            currentCompany={currentCompany}
          />
          /*
          <EventCreationForm
            jobName={interviewData.name}
            jobId={interviewData.id}
            candidates={interviewData.candidates}
            onClose={handleCloseModal}
            currentCompany={currentCompany}
          />
          */
        );
      case "note":
        return (
          <NoteAddForm
            jobseekerId={selectedJobseekers}
            companyUserId={currentUser}
            onSubmit={handleNoteSubmit}
          />
        );
      case "message":
        return (
          <MessageInterface
            worker={selectedJobseekers}
            companyUser={currentUser}
            initialJobseekers={initialJobseekers}
          />
        );
      case "tag":
        return (
          <div className="w-full">
            <h3 className="font-lg font-bold pb-4">Add Tags</h3>
            <TagInterface
              jobseekers={selectedJobseekers}
              companyUser={currentUser}
              currentCompany={currentCompany}
              jobseekerList={initialJobseekers}
              handleAddTags={handleAddTags}
            />
          </div>
        );
      case "instantCall":
        return (
          <InstantVideoCallModal
            callData={instantCallData}
            onClose={handleCloseModal}
            onSendEmail={handleSendInstantCallEmail}
          />
        );
      default:
        return null;
    }
  };

  const handleSortBy = (sortBy) => {
    resetSortUI();
    let sortIndex = "";
    switch (sortBy) {
      case "name":
        setSortName(true);
        sortIndex = "name";
        break;
      case "appliedBy":
        setSortApplied(true);
        sortIndex = "createdAt";
        break;
      case "qStatus":
        setSortQStatus(true);
        sortIndex = "questionnaireStatus";
        break;
      case "score":
        setSortScore(true);
        sortIndex = "score";
        break;
    }
    const list = sortedJobseekers(filteredJobseekers, sortBy, sortOrder);
    setJobseekers(list);
    setSortOrder(() => {
      return sortOrder === "asc" ? "desc" : "asc";
    });
  };

  const resetSortUI = () => {
    setSortScore(false);
    setSortQStatus(false);
    setSortApplied(false);
    setSortName(false);
  };

  const resetSorting = (e) => {
    e.preventDefault();
    e.stopPropagation();
    resetSortUI();
    const list = sortedJobseekers(initialJobseekers);
    setJobseekers(list);
  };

  const handleSearch = (e) => {
    resetSortUI();
    const searchFor = e.target.value;
    let searchjobseekers = initialJobseekers;

    const jobseekers = searchjobseekers.filter((jobseeker) => {
      if (jobseeker.name.toLowerCase().includes(searchFor)) {
        return true;
      }
      if (jobseeker.jobTitle?.toLowerCase().includes(searchFor)) {
        return true;
      }
      if (jobseeker.location?.toLowerCase().includes(searchFor)) {
        return true;
      }
      return false;
    });
    setJobseekers(() => {
      return sortedJobseekers(jobseekers);
    });
  };

  const removeTag = (jobseekerId, tagId) => {
    const jobseekers = filteredJobseekers.map((j) => {
      if (j._id === jobseekerId) {
        const f_tags = j.tags.filter((tag) => {
          return tag._id === tagId ? false : true;
        });
        j.tags = f_tags;
      }
      return j;
    });
    setJobseekers(jobseekers);
  };

  const handleTagDelete = async (jobseekerId, tagId) => {
    await deleteJobseekerTag(tagId);
    removeTag(jobseekerId, tagId);
  };

  return (
    <div className="container mx-auto p-4">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-4 gap-4">
        <h2 className="text-2xl font-bold">Jobseekers</h2>
        <div className="flex flex-col w-full lg:w-4/5 gap-3 lg:flex-row lg:items-center lg:justify-end">
          <div
            className="flex items-center cursor-pointer w-full sm:w-auto"
            onClick={handleSelectAllChange}
          >
            <input
              type="checkbox"
              checked={selectAll}
              onChange={handleSelectAllChange}
              className="w-6 h-6 mr-2 rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
            />
            <label className="cursor-pointer text-sm md:text-base">Select All</label>
          </div>
          <div className="w-full md:w-auto">
            <JobseekerStatusMultiSelect
              onChange={(selectedOptions) => {
                if (selectedOptions.length === 0) {
                  setFilterStatus(["all"]);
                  setFilterQuestionnaireStatus(["all"]);
                  return true;
                }
                const statusFilter = [
                  "Applied",
                  "Interviewed",
                  "Interview Booked",
                  "Hired",
                  "Rejected",
                ];
                let filterStatus = [];
                let filterQuestionnaireStatus = [];
                selectedOptions.forEach((status) => {
                  if (statusFilter.includes(status.value)) {
                    filterStatus.push(status.value);
                  } else {
                    filterQuestionnaireStatus.push(status.value);
                  }
                });
                if (filterStatus.length === 0) filterStatus = ["all"];
                if (filterQuestionnaireStatus.length === 0)
                  filterQuestionnaireStatus = ["all"];

                setFilterStatus(filterStatus);
                setFilterQuestionnaireStatus(filterQuestionnaireStatus);
              }}
            />
          </div>
          <div className="w-full md:w-60">
            <Select
              isMulti={true}
              name="tags"
              options={tags}
              className="basic-multi-select"
              placeholder="Filter by Tags"
              onChange={(selectedOptions) => {
                if (selectedOptions.length === 0) {
                  setFilterTags(["all"]);
                  return true;
                }
                setFilterTags(selectedOptions.map((option) => option.value));
              }}
            />
          </div>
          <select
            onChange={(e) => handleAction(e.target.value)}
            className="border rounded p-2 w-full md:w-auto text-sm md:text-base"
            disabled={selectedJobseekers.length === 0}
            value={bulkSelect}
          >
            <option value="">Bulk Actions</option>
            <option value="Invite to Interview">Invite to Interview</option>
            <option value="Generate Instant Video Call Link">Generate Instant Video Call Link</option>
            <option value="Chase up Questionnaire">
              Chase up Questionnaire
            </option>
            <option value="Reject">Reject</option>
            <option value="Add Notes">Add Notes</option>
            <option value="Mark as Hired">Mark as Hired</option>
            {/*
            <option value="Call">Call</option>*/}
            <option value="Message">Message</option>
            <option value="Ask Further Questions">Ask Further Questions</option>
          </select>
        </div>
      </div>
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <nav className="bg-white shadow-sm p-2 md:p-4">
          <div className="max-w-8xl">
            {/* Mobile: Search only */}
            <div className="md:hidden px-2">
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                placeholder="Search name, title, location"
                onChange={handleSearch}
              />
            </div>

            {/* Desktop: Full navigation with search and sort buttons */}
            <div className="hidden md:flex items-center justify-between w-full px-4 text-sm">
              <div className="flex gap-2 w-full md:w-1/3">
                <button
                  onClick={() => {
                    handleSortBy("name");
                  }}
                  className="flex items-center gap-1 px-3 py-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors duration-200 whitespace-nowrap"
                >
                  Name
                  <ArrowUpDown className="h-4 w-4" />
                  {sortName ? (
                    <a
                      href="#"
                      onClick={resetSorting}
                      className="hover:shadow-lg px-2 py-2 rounded-md transition-colors duration-200"
                    >
                      <X className="h-4 w-4" />
                    </a>
                  ) : (
                    ""
                  )}
                </button>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="search name, title and location"
                  onChange={handleSearch}
                />
              </div>
              <div className="flex items-center">
                <div className="flex items-center space-x-2 lg:space-x-6">
                  <button
                    onClick={() => {
                      handleSortBy("appliedBy");
                    }}
                    className="flex items-center gap-1 px-2 lg:px-3 py-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors duration-200 whitespace-nowrap text-xs lg:text-sm"
                  >
                    Applied
                    <ArrowUpDown className="h-4 w-4" />
                    {sortApplied ? (
                      <a
                        href="#"
                        onClick={resetSorting}
                        className="hover:shadow-lg px-2 py-2 rounded-md transition-colors duration-200"
                      >
                        <X className="h-4 w-4" />
                      </a>
                    ) : (
                      ""
                    )}
                  </button>
                  <button
                    onClick={() => {
                      handleSortBy("qStatus");
                    }}
                    className="flex items-center gap-1 px-2 lg:px-3 py-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors duration-200 whitespace-nowrap text-xs lg:text-sm"
                  >
                    Q.Status
                    <ArrowUpDown className="h-4 w-4" />
                    {sortQStatus ? (
                      <a
                        href="#"
                        onClick={resetSorting}
                        className="hover:shadow-lg px-2 py-2 rounded-md transition-colors duration-200"
                      >
                        <X className="h-4 w-4" />
                      </a>
                    ) : (
                      ""
                    )}
                  </button>
                  {/* added to maintain gaps */}
                  <button className="hidden lg:flex items-center gap-2 px-3 py-2"></button>
                  {/* added to maintain gaps */}
                </div>
                <div className="ml-2 lg:ml-8">
                  <button
                    onClick={() => {
                      handleSortBy("score");
                    }}
                    className="flex items-center gap-1 px-2 lg:px-3 py-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors duration-200 whitespace-nowrap text-xs lg:text-sm"
                  >
                    Score
                    <ArrowUpDown className="h-4 w-4" />
                    {sortScore ? (
                      <a
                        href="#"
                        onClick={resetSorting}
                        className="hover:shadow-lg px-2 py-2 rounded-md transition-colors duration-200"
                      >
                        <X className="h-4 w-4" />
                      </a>
                    ) : (
                      ""
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </nav>
        {filteredJobseekers.length === 0 ? (
          <div className="text-center py-4">
            No jobseekers found for this job.
          </div>
        ) : (
          ""
        )}
        {filteredJobseekers.map((jobseeker) => (
          <div
            key={jobseeker._id}
            className="border-b border-gray-200 last:border-b-0"
          >
            <div className="flex items-start p-3 md:p-4 hover:bg-gray-50 gap-2 md:gap-4">
              {/* Checkbox */}
              <div className="p-2 -m-2 min-w-[44px] min-h-[44px] flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={selectedJobseekers.some(
                    (element) => element.id == jobseeker._id,
                  )}
                  onChange={() =>
                    handleCheckboxChange(
                      jobseeker._id,
                      jobseeker.name,
                      jobseeker,
                    )
                  }
                  className="w-5 h-5 md:w-6 md:h-6 rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                />
              </div>

              {/* Avatar */}
              {jobseeker.avatar ? (
                <img
                  src={`${API_BASE_URL}${jobseeker.avatar?.replace(/^\//, "")}`}
                  alt={jobseeker.name}
                  className="w-12 h-12 md:w-16 md:h-16 rounded-full object-cover cursor-pointer flex-shrink-0"
                  onClick={() =>
                    setExpandedJobseeker(
                      expandedJobseeker === jobseeker._id
                        ? null
                        : jobseeker._id,
                    )
                  }
                />
              ) : (
                <span
                  className="w-12 h-12 md:w-16 md:h-16 bg-indigo-500 text-white rounded-full flex items-center justify-center font-bold text-xs md:text-sm cursor-pointer flex-shrink-0"
                  onClick={() =>
                    setExpandedJobseeker(
                      expandedJobseeker === jobseeker._id
                        ? null
                        : jobseeker._id,
                    )
                  }
                >
                  {getNameInitials(jobseeker.name)}
                </span>
              )}

              {/* Main content area - split into two sections for mobile */}
              <div className="flex-grow min-w-0">
                {/* Name and job info */}
                <div
                  className="cursor-pointer mb-2"
                  onClick={() =>
                    setExpandedJobseeker(
                      expandedJobseeker === jobseeker._id ? null : jobseeker._id,
                    )
                  }
                >
                  <h3 className="font-semibold text-sm md:text-base">
                    {capitalizeWords(jobseeker.name)}
                  </h3>
                  <p className="text-xs md:text-sm text-gray-500">
                    {jobseeker.jobTitle
                      ? `${jobseeker.jobTitle} • ${jobseeker.location}`
                      : " - "}
                  </p>
                  <div className="block mt-1">
                    {jobseeker.tags.map((tag) => (
                      <TagComponent
                        key={tag._id}
                        text={tag.tag.name}
                        onDelete={(e) => {
                          e.stopPropagation();
                          handleTagDelete(jobseeker._id, tag._id);
                        }}
                      />
                    ))}
                  </div>
                </div>

                {/* Status badges - stacked on mobile, row on desktop */}
                <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-3">
                  <span
                    title="applied on"
                    className="px-2 py-1 rounded-full text-xs w-auto md:w-24 text-center inline-block"
                  >
                    {new Date(jobseeker.createdAt).toLocaleDateString()}
                  </span>
                  <span
                    onMouseEnter={() => setTooltipOpen(true)}
                    onMouseOut={() => setTooltipOpen(false)}
                    data-tooltip-id="questionnaireStatusTooltip"
                    data-tooltip-content={`Questionnaire: ${jobseeker.detailedInfo.questionnaireResponsesCompletedBy ? jobseeker.detailedInfo.questionnaireResponsesCompletedBy : ""} (${jobseeker.questionnaireCompletionRate}%)`}
                    className={`px-2 py-1 rounded-full text-xs ${jobseeker.questionnaireStatus ? getStatusColor(jobseeker.questionnaireStatus) : ""} w-auto md:w-24 text-center inline-block`}
                  >
                    {jobseeker.questionnaireStatus
                      .replace("_", " ")
                      .charAt(0)
                      .toUpperCase() +
                      jobseeker.questionnaireStatus.replace("_", " ").slice(1)}
                  </span>
                  {jobseeker.questionnaireCompletionRate ? (
                    <ToolTipHtml
                      id="questionnaireStatusTooltip"
                      isOpen={tooltipOpen}
                    />
                  ) : (
                    ""
                  )}
                  <span
                    className={`px-2 py-1 rounded-full text-xs ${jobseeker.applicationStatus ? getApplicationStatusColor(jobseeker.applicationStatus) : ""} w-auto md:w-24 text-center inline-block`}
                  >
                    {jobseeker.applicationStatus.charAt(0).toUpperCase() +
                      jobseeker.applicationStatus.slice(1)}
                  </span>
                </div>
              </div>

              {/* Score and Action Menu - always visible on right */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <div
                  className={`w-10 h-10 md:w-12 md:h-12 rounded-full border-2 ${getScoreColor(jobseeker.score ? jobseeker.score : 0)} flex items-center justify-center font-semibold text-sm md:text-base`}
                >
                  {jobseeker.score ?? 0}
                </div>
                <ActionMenu
                  jobseekerId={jobseeker._id}
                  jobseekerName={jobseeker.name}
                  jobseeker={jobseeker}
                  onAction={handleAction}
                />
              </div>
            </div>
            {expandedJobseeker === jobseeker._id && (
              <ExpandedJobseekerView
                jobseeker={jobseeker}
                selectedJob={selectedJob}
                jobseekersAverageScore={jobseekerAverageScore}
                initialJobseekers={initialJobseekers}
                companyUser={currentUser}
              />
            )}
          </div>
        ))}
      </div>
      <Modal
        isOpen={modalState.isOpen}
        onRequestClose={handleCloseModal}
        contentLabel={`JobSeeker Action: ${modalState.type}`}
        className={`w-auto mx-auto mt-20 p-6 bg-white rounded-lg shadow-xl ${modalState.type === "message" ? "min-w-1/3" : ""}`}
        overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
      >
        {renderModalContent()}
      </Modal>
    </div>
  );
};

export default JobseekerList;
